import useScrollReveal from '@/hooks/useScrollReveal';
import SectionHeading from '@/components/feature/SectionHeading';

const galleryImages = [
  {
    src: 'https://public.readdy.ai/ai/img_res/edited_90ef0a8af71baaf7af0995cfea615695_3ddaa4a9.jpg',
    alt: 'Notranjost lokala Guma Caffe Lounge Bar z mizami, stoli in osvetljenim šankom',
    title: 'Notranjost Guma Caffe Lounge Bar Gorišnica – ambient za kavo',
  },
  {
    src: 'https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/a8aec268cac0192721b3d5189e84d7be.png',
    alt: 'Prostorna notranjost lokala Guma Caffe Lounge Bar s sedeži, televizijo in cenikom',
    title: 'Prostor za druženje v Guma Caffe Lounge Bar Zamušani',
  },
  {
    src: 'https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/84c931a8e966565c42b5cec347d0f6fd.png',
    alt: 'Ledeno hladne pijače Salitos v vedru z ledom in svežim sadjem',
    title: 'Osvežilne pijače in pivo v Guma Caffe Lounge Bar Gorišnica',
  },
  {
    src: 'https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/a4a84974d868f72c55958079e5b70b0b.png',
    alt: 'Dve osvežilni pijači z limeto v kozarcih na terasi lokala',
    title: 'Terasa Guma Caffe Lounge Bar Gorišnica – osvežitev v senci',
  },
  {
    src: 'https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/75a073fda89801119f124fa751978880.png',
    alt: 'Jägermeister in Coca-Cola v posodi z ledom, limeto in pomarančo',
    title: 'Žgane pijače in koktejli v Guma Caffe Lounge Bar Zamušani',
  },
  {
    src: 'https://static.readdy.ai/image/610b801a81a299b5cf2b47908013d228/3a2f1cbe0626d755c2a35083f8b69d12.png',
    alt: 'Dva ledeno hladna zelena koktejla z rezino pomaranče in slamico',
    title: 'Vikend koktejli v Guma Caffe Lounge Bar Gorišnica',
  },
];

const reviews = [
  {
    text: 'Brez dvoma najboljša kava v celotni okolici. Vedno prijetno vzdušje, prijazno osebje in odlična terasa. Moja prva izbira za jutranji espresso!',
    author: 'Marko P.',
    role: 'Redni gost',
  },
  {
    text: 'Guma Bar je srce Zamušanov. Nagrada za naj kavo je popolnoma zaslužena — kava je vrhunska, cene prijazne, terasa pa kot nalašč za sprostitev po napornem dnevu.',
    author: 'Tina K.',
    role: 'Domačinka',
  },
  {
    text: 'Medtem ko čakam na servis avta v bližnji vulkanizerski delavnici, vedno skočim na hitro kavo. Prijazna postrežba, hladno pivo in super ambient. Priporočam vsem!',
    author: 'Robert M.',
    role: 'Obiskovalec',
  },
];

export default function GallerySection() {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 });

  return (
    <section id="galerija" className="relative py-16 md:py-24 px-4 md:px-6 bg-background-100 scroll-mt-16 md:scroll-mt-20" ref={ref}>
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <SectionHeading
          eyebrow="Galerija"
          title="Pokukajte v naš svet"
          description="Nekaj utrinkov iz Guma Bara — vzdušje, pijača in trenutki, ki jih želimo deliti s tabo."
        />

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {galleryImages.map((image, idx) => (
            <div
              key={idx}
              className={`group rounded-lg overflow-hidden transition-all duration-700 ${
                idx <= 1 ? 'col-span-2' : 'col-span-1'
              } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
              style={{ transitionDelay: `${200 + idx * 120}ms` }}
            >
              <img
                src={image.src}
                alt={image.alt}
                title={image.title}
                className={`w-full object-cover object-center rounded-lg transition-transform duration-700 group-hover:scale-105 ${
                  idx === 0 ? 'h-[240px] md:h-[440px]' : idx === 1 ? 'h-[220px] md:h-[360px]' : 'h-[200px] md:h-[300px]'
                }`}
              />
            </div>
          ))}
        </div>

        {/* Reviews */}
        <div
          className={`mt-16 md:mt-20 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="h-px w-8 bg-primary-500/70" aria-hidden="true"></span>
              <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-primary-400">
                Mnenja gostov
              </span>
              <span className="h-px w-8 bg-primary-500/70" aria-hidden="true"></span>
            </div>
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
              Kaj pravijo naši gostje
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {reviews.map((review, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 hover:border-primary-500/40 transition-colors duration-300"
              >
                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-primary-500 text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-foreground-300 text-sm leading-relaxed italic">
                  &ldquo;{review.text}&rdquo;
                </p>
                <div className="mt-4 pt-4 border-t border-background-100">
                  <p className="text-sm font-semibold text-foreground-100">{review.author}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{review.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}